import React, { useState, useEffect } from "react";
import { Container, Box, Typography, Button, Chip, TextField, FormControlLabel, Checkbox, FormGroup, List, ListItem, ListItemText } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router-dom";
import Plot from "react-plotly.js";
import { useAuth0 } from "@auth0/auth0-react";
import '../styles/individualPatientTest.css';

const toNumOrNull = (v) => {
    const s = String(v ?? "").trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const toBool01 = (v) => String(v ?? "").trim() === "1";

const buildScreeningEarData = (testRow) => {
    const freqs = [1000, 2000, 4000, 6000];

    const Left = {};
    const Right = {};

    for (const f of freqs) {
        Left[f] = toBool01(testRow[`hz_${f}_passed_left`]);
        Right[f] = toBool01(testRow[`hz_${f}_passed_right`]);
    }

    return { Left, Right };
};


function HearingChart({ test, isScreening }) {
    const data = buildScreeningEarData(test);
    const left = data?.Left || {};
    const right = data?.Right || {};
    let freqs, leftY, rightY, layout;

    if (isScreening) {
        freqs = [1000, 2000, 4000, 6000];
        leftY = freqs.map(f => left[f] ? 1 : 0);
        rightY = freqs.map(f => right[f] ? 1 : 0);

        layout = {
            xaxis: { title: "Frequency (Hz)" },
            yaxis: {
                title: "Result",
                range: [-0.2, 1.2],
                tickvals: [0, 1],
                ticktext: ["&#10060; Fail", "&#9989; Pass"],
            },
            height: 380,
            legend: { orientation: "h", y: -0.2 },
        };

    } else {

        freqs = [500, 1000, 2000, 4000, 6000, 8000];
        leftY = freqs.map((f) => toNumOrNull(test[`left_threshold_${f}`]));
        rightY = freqs.map((f) => toNumOrNull(test[`right_threshold_${f}`]));

        layout = {
            xaxis: { title: { text: "Frequency (Hz)" } },
            yaxis: {
                title: "Result (dB HL)",
                range: [120, -10],
                tickvals: [-10, 0, 20, 40, 60, 80, 100, 120],
            },
            height: 380,
            legend: { orientation: "h", y: -0.2 },
        };
    }

    const leftTrace = {
        x: freqs,
        y: leftY,
        name: "Left Ear",
        type: "scatter",
        mode: "lines+markers",
        marker: { color: "blue", size: 7, symbol: "x" },
        line: { color: "blue" }
    };

    const rightTrace = {
        x: freqs,
        y: rightY,
        name: "Right Ear",
        type: "scatter",
        mode: "lines+markers",
        marker: { color: "red", size: 7, symbol: "circle-open" },
        line: { color: "red" }
    };

    const config = {
        responsive: true,
        displaylogo: false,
        modeBarButtonsToRemove: [
            'zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d',
            'resetScale2d',
            'hoverClosestCartesian', 'hoverCompareCartesian', 'toggleSpikelines'
        ],
    }


    return <Plot data={[leftTrace, rightTrace]} layout={layout} config={config} className="hearing-chart-plot" />;
}

const map_questionnaire = {
    q1: { "Yes": 1, "No": 2, "I'm not sure": 3 },
    q1a: {
        "Within the past year": 1,
        "Within the past 2 - 5 years": 2,
        "Within the past 5 - 10 years": 3,
        "More than 10 years ago": 4
    },
    q1b: {
        "I have no concerns about my hearing": 1,
        "I think I might have had hearing loss but it's not bothersome enough to do anything about it right now": 2,
        "I think I might have hearing loss but I don't have the resources to do anything about it right now": 3,
        "I think I might have hearing loss but I don't know where to go for help": 4,
        "I think I might have hearing loss but I don't trust the healthcare system": 5
    },
    q2: { "Yes": 1, "No": 0 },
    q2a: {
        "Right Ear": 1,
        "Left Ear": 2,
        "Both Ears": 3
    },
    q2b: {
        "Right Ear": 1,
        "Left Ear": 2,
        "Both Ears": 3
    },
    q3: { Yes: 1, No: 0 },
    q3a: {
        "Impulse - lasting seconds": 1,
        "Intermittent - lasting minutes": 2,
        "Continuous - lasting hours": 3
    },
    q3b: {
        "Only a few instances": 1,
        "More than a few instances, but spaced over time": 2,
        "Prolonged or continuous exposure over months": 3,
        "Prolonged or continuous exposure over years": 4,
    },
    q4: { Yes: 1, No: 0 },
    q5: {
        "Yes, me": 1,
        "Yes, immediate family member": 2,
        No: 3
    },
    q6: { Yes: 1, No: 0 },
    q6a: {
        "Left, constant": 1,
        "Left, intermittent": 2,
        "Right, constant": 3,
        "Right, intermittent": 4,
    },
    q6bl: { "1": 1, "2": 2, "3": 3, "4": 4, "5": 5 },
    q6br: { "1": 1, "2": 2, "3": 3, "4": 4, "5": 5 },
    q6c: { Yes: 1, No: 0 },
    q7: {
        "High blood pressure": 1,
        "Diabetes": 2,
        "Cardiovascular (heart) disease": 3,
        "Stroke": 4,
        "Dementia / Alzheimer's": 5,
        "Depression": 6
    },
    q8: { "Yes": 1, "No": 0 },
    q8a: {
        "Spinning": 1,
        "Floating": 2,
        "Lightheaded": 3,
        "Disorientation": 4,
        "Off balance or unsteady": 5
    },
};

// Utility: invert {key -> value} to {value -> key}
const invertMap = (obj) =>
    Object.fromEntries(Object.entries(obj).map(([k, v]) => [String(v), k]));

// Build reverse maps
const reverse_map = Object.fromEntries(
    Object.entries(map_questionnaire).map(([q, m]) => [q, invertMap(m)])
);

// Decode function: payload -> answers "a"
function decodeQuestionnaireFromPayload(payload) {
    const a = {};

    if (payload.had_hearing_tested !== undefined && payload.had_hearing_tested !== null) {
        a.q1 = reverse_map.q1[String(payload.had_hearing_tested)];
    }

    if (payload.when_last_test !== undefined && payload.when_last_test !== null) {
        a.q1a = reverse_map.q1a[String(payload.when_last_test)];
    }

    if (payload.think_diagnosed_hearing_loss !== undefined && payload.think_diagnosed_hearing_loss !== null) {
        a.q2 = reverse_map.q2[String(payload.think_diagnosed_hearing_loss)];
    }

    if (payload.think_hearing_loss_in !== undefined && payload.think_hearing_loss_in !== null) {
        a.q2a = reverse_map.q2a[String(payload.think_hearing_loss_in)];
    }

    if (payload.diagnosed_hearing_loss_in !== undefined && payload.diagnosed_hearing_loss_in !== null) {
        a.q2b = reverse_map.q2b[String(payload.diagnosed_hearing_loss_in)];
    }

    if (payload.exposed_to_loud_noises !== undefined && payload.exposed_to_loud_noises !== null) {
        a.q3 = reverse_map.q3[String(payload.exposed_to_loud_noises)];
    }

    if (payload.type_of_loud_noise !== undefined && payload.type_of_loud_noise !== null) {
        a.q3a = reverse_map.q3a[String(payload.type_of_loud_noise)];
    }

    if (payload.duration_of_noise_exposure !== undefined && payload.duration_of_noise_exposure !== null) {
        a.q3b = reverse_map.q3b[String(payload.duration_of_noise_exposure)];
    }

    if (payload.has_ear_pain !== undefined && payload.has_ear_pain !== null) {
        a.q4 = reverse_map.q4[String(payload.has_ear_pain)];
    }

    if (payload.has_had_tinnitus !== undefined && payload.has_had_tinnitus !== null) {
        a.q6 = reverse_map.q6[String(payload.has_had_tinnitus)];
    }

    if (payload.bothersome_tinnitus_left !== undefined && payload.bothersome_tinnitus_left !== null) {
        a.q6bl = reverse_map.q6bl[String(payload.bothersome_tinnitus_left)];
    }

    if (payload.bothersome_tinnitus_right !== undefined && payload.bothersome_tinnitus_right !== null) {
        a.q6br = reverse_map.q6br[String(payload.bothersome_tinnitus_right)];
    }

    if (payload.tinnitus_heartbeat !== undefined && payload.tinnitus_heartbeat !== null) {
        a.q6c = reverse_map.q6c[String(payload.tinnitus_heartbeat)];
    }

    if (payload.balance_issues !== undefined && payload.balance_issues !== null) {
        a.q8 = reverse_map.q8[String(payload.balance_issues)];
    }

    // Multi-select fields:
    // q1b -> payload.why_no_test___{num} = 1
    const no_test = [];
    for (const [k, v] of Object.entries(payload)) {
        const m = k.match(/^why_no_test___(\d+)$/);
        if (m && Number(v) === 1) {
            const code = m[1]; // "1".."5"
            const choice = reverse_map.q1b[code];
            if (choice) no_test.push(choice);
        }
    }
    if (no_test.length) a.q1b = no_test;

    // q5 -> payload.had_ear_surgery___{num} = 1
    const ear_surgery = [];
    for (const [k, v] of Object.entries(payload)) {
        const m = k.match(/^had_ear_surgery___(\d+)$/);
        if (m && Number(v) === 1) {
            const code = m[1]; // "1".."3"
            const choice = reverse_map.q5[code];
            if (choice) ear_surgery.push(choice);
        }
    }
    if (ear_surgery.length) a.q5 = ear_surgery;

    // q6a -> payload.which_ear_tinnitus___{num} = 1
    const ear_tinnitus = [];
    for (const [k, v] of Object.entries(payload)) {
        const m = k.match(/^which_ear_tinnitus___(\d+)$/);
        if (m && Number(v) === 1) {
            const code = m[1]; // "1".."4"
            const choice = reverse_map.q6a[code];
            if (choice) ear_tinnitus.push(choice);
        }
    }
    if (ear_tinnitus.length) a.q6a = ear_tinnitus;

    // q7 -> payload.any_diagnoses___{num} = 1
    const diagnoses = [];
    for (const [k, v] of Object.entries(payload)) {
        const m = k.match(/^any_diagnoses___(\d+)$/);
        if (m && Number(v) === 1) {
            const code = m[1]; // "1".."6"
            const choice = reverse_map.q7[code];
            if (choice) diagnoses.push(choice);
        }
    }
    if (diagnoses.length) a.q7 = diagnoses;

    // q8a -> balance_symptoms___{num} = 1
    const symptoms = [];
    for (const [k, v] of Object.entries(payload)) {
        const m = k.match(/^balance_symptoms___(\d+)$/);
        if (m && Number(v) === 1) {
            const code = m[1]; // "1".."5"
            const choice = reverse_map.q8a[code];
            if (choice) symptoms.push(choice);
        }
    }
    if (symptoms.length) a.q8a = symptoms;

    return a;
}

function getFollowupStatusChip(test) {
    const referral = String(test?.received_referral ?? "").trim();
    const sought = String(test?.received_follow_up ?? "").trim();

    if (referral === "") return { label: "Follow-up: Unknown", color: "default" };
    if (referral === "0") return { label: "Follow-up: Not needed", color: "success" };

    if (referral === "1") {
        const today = new Date();
        if (sought !== "") return { label: `Follow-up: Completed On ${test?.followup_entered_date ?? today.toLocaleDateString('en-US')}`, color: "success" }
        return { label: "Follow-up: Needed", color: "warning" };
    }

    return { label: "Follow-up: Unknown", color: "default" };
}

const IndividualPatientTest = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isScreening = location.state?.isScreening;
    const test = location.state?.test || {};
    const a = decodeQuestionnaireFromPayload(test);
    const patient = location.state?.patient || {};
    const { getAccessTokenSilently } = useAuth0();

    const [savingFollowup, setSavingFollowup] = useState(false);
    const [saveError, setSaveError] = useState("");
    const followUpChipInitial = getFollowupStatusChip(test);
    const [followChip, setFollowChip] = useState(followUpChipInitial);

    const followupKey = test?.id ? `followup:${test.id}` : null;

    const [fuForm, setFuForm] = useState(() => {
        if (followupKey) {
            try {
                const saved = JSON.parse(localStorage.getItem(followupKey) || "null");
                if (saved) return saved;
            } catch { }
        }

        return {
            received_referral: String(test?.received_referral ?? "").trim(),
            followup_sought: String(test?.received_follow_up ?? "").trim(),
            followup_why_not: String(test?.why_no_follow_up ?? "").trim(),
            followup_where: String(test?.where_receive_follow_up ?? "").trim(),
            followup_when: String(test?.when_receive_follow_up ?? "").trim(),
            followup_entered_date: String(test?.followup_entered_date ?? "").trim(),
            result_medical: String(test?.follow_up_result___1 ?? "").trim() === "1",
            result_hl_confirmed: String(test?.follow_up_result___2 ?? "").trim() === "1",
            result_devices: String(test?.follow_up_result___3 ?? "").trim() === "1",
            result_vestibular: String(test?.follow_up_result___4 ?? "").trim() === "1",
        };
    });

    useEffect(() => {
        if (!followupKey) return;
        try {
            const saved = JSON.parse(localStorage.getItem(followupKey) || "null");
            if (saved) setFuForm(saved);
        } catch { }
    }, [followupKey]);

    const saveFollowup = async () => {
        setSaveError("");

        if (referralMadeRaw !== "1") {
            alert("No referral was made — nothing to save.");
            return;
        }

        const follow_up_result = [];
        if (fuForm.result_medical) follow_up_result.push(1);
        if (fuForm.result_hl_confirmed) follow_up_result.push(2);
        if (fuForm.result_devices) follow_up_result.push(3);
        if (fuForm.result_vestibular) follow_up_result.push(4);

        const patient_id = test.patient_id;
        const redcap_event_name = test?.redcap_event_name;
        const redcap_repeat_instance = test?.redcap_repeat_instance;

        if (!patient_id) {
            setSaveError("Missing patient_id (could not find test.patient_id / patient.patient_id / test.id).");
            return;
        }
        if (!redcap_event_name) {
            setSaveError("Missing redcap_event_name (could not find test.redcap_event_name).");
            return;
        }

        if (fuForm.followup_sought === "0" && !String(fuForm.followup_why_not ?? "").trim()) {
            setSaveError("Please fill in 'If no, why not?' before saving.");
            return;
        }

        const body = {
            patient_id,
            redcap_event_name,
            redcap_repeat_instance,
            received_follow_up: fuForm.followup_sought,                // "1" | "0"
            where_receive_follow_up: fuForm.followup_where || "",
            when_receive_follow_up: fuForm.followup_when || "",        // string
            why_no_follow_up: fuForm.followup_why_not || "",           // string
            follow_up_result,                                          // [1,2,3,4]
        };

        setSavingFollowup(true);
        try {
            const token = await getAccessTokenSilently();

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URI}/api/results/follow-up`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });

            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                data = { raw: text };
            }

            if (!res.ok) {
                setSaveError(data?.error || data?.detail || `Save failed (${res.status}).`);
                return;
            }

            alert("Follow-up saved to server.");
        } catch (err) {
            setSaveError(err?.message || "Save failed (network/auth error).");
        } finally {
            const today = new Date();
            setSavingFollowup(false);
            setFollowChip({ label: `Follow-up: Completed On ${test?.followup_entered_date ?? today.toLocaleDateString('en-US')}`, color: "success" })
        }
    };

    const convertReferralsToArray = () => {
        const referralObjects = [];
        if (test?.referral_type___1 !== "0") {
            referralObjects.push(["ENT", test?.reasons_ent.split("|")]);
        }
        if (test?.referral_type___2 !== "0") {
            referralObjects.push(["Audiologist", test?.reasons_audiologist.split('|')]);
        }
        if (test?.referral_type___3 !== "0") {
            referralObjects.push(["Tinnitus Management Recommendations", test?.reasons_tinnitusrecs.split('|')]);
        }
        if (test?.referral_type___4 !== "0") {
            referralObjects.push(["Psychologist", test?.reasons_psychologist.split('|')]);
        }
        if (test?.referral_type___5 !== "0") {
            referralObjects.push(["Hearing Aids", test?.reasons_hearingaids.split("|")]);
        }

        return referralObjects;
    }

    /* follow-up decoded */
    const referralMadeRaw = String(test?.received_referral ?? "").trim();
    let referralObjects = [];
    if (referralMadeRaw) {
        referralObjects = convertReferralsToArray()
    }

    return (
        <Container maxWidth="md">
            <Box className="ipt-back-row">
                <Button
                    onClick={() => navigate(-1)}
                    startIcon={<ArrowBackIcon />}
                    variant="text"
                    className="ipt-back-btn"
                >
                    Back to Patient
                </Button>
            </Box>

            <Box className="ipt-header-row" >
                <Typography>Patient Name: {`${patient.first_name} ${patient.last_name}` ?? "N/A"}</Typography>
                <Chip label={followChip.label} color={followChip.color} variant="outlined" />
            </Box>

            <Typography
                variant="h5"
                className="ipt-results-title"
            >
                Test Results:
            </Typography>
            <HearingChart test={test} isScreening={isScreening} />

            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />} className="ipt-accordion-summary">
                    Questionnaire Answers
                </AccordionSummary>

                <AccordionDetails>
                    <Typography><b>Have you ever had your hearing tested?</b> {a?.q1 ?? "N/A"}</Typography>
                    <Typography><b>When did you last have your hearing tested?</b> {a?.q1a ?? "N/A"}</Typography>
                    <Typography><b>Why not?</b> {a?.q1b?.length ? a.q1b.join(", ") : "N/A"}</Typography>

                    <Typography className="ipt-mt-1"><b>Do you think you might have hearing loss, or have you previously been diagnosed with hearing loss?</b> {a?.q2 ?? "N/A"}</Typography>
                    <Typography><b>I think I might have hearing loss in:</b> {a?.q2a ?? "N/A"}</Typography>
                    <Typography><b>I have been diagnosed with hearing loss in:</b> {a?.q2b ?? "N/A"}</Typography>

                    <Typography className="ipt-section-spacer"><b>Have you been exposed to loud noises (e.g. power tools, firearms, farm equipment, explosions, music concerts)?</b> {a?.q3 ?? "N/A"}</Typography>
                    <Typography><b>What type of noise have you been exposed to most often?</b> {a?.q3a ?? "N/A"}</Typography>
                    <Typography><b>What is the approximate duration of your noise exposure?</b> {a?.q3b ?? "N/A"}</Typography>

                    <Typography className="ipt-section-spacer"><b>Do you have any ear pain, ear fullness, or drainage?</b> {a?.q4 ?? "N/A"}</Typography>
                    <Typography><b>Have you or an immediate family member had ear surgery?</b> {a?.q5?.length ? a.q5.join(", ") : "N/A"}</Typography>

                    <Typography className="ipt-section-spacer"><b>Have you ever had any tinnitus (ringing, buzzing, or roaring) in your ears?</b> {a?.q6 ?? "N/A"}</Typography>
                    <Typography><b>Which ear have you had tinnitus?</b> {a?.q6a?.length ? a.q6a.join(", ") : "N/A"}</Typography>
                    <Typography><b>How bothersome is your tinnitus in your left ear?</b> {a?.q6bl ?? "N/A"}</Typography>
                    <Typography><b>How bothersome is your tinnitus in your right ear?</b> {a?.q6br ?? "N/A"}</Typography>
                    <Typography><b>Does the tinnitus sound like your heartbeat?</b> {a?.q6c ?? "N/A"}</Typography>

                    <Typography className="ipt-mt-1"><b>Do you have any of the following diagnoses? (Check all that apply)</b> {a?.q7?.length ? a.q7.join(", ") : "N/A"}</Typography>

                    <Typography className="ipt-mt-1"><b>In the past 6 months, have you experienced dizziness, balance problems, or balance-related falls?</b> {a?.q8 ?? "N/A"}</Typography>
                    <Typography><b>How would you describe your symptoms? (Check all that apply)</b> {a?.q8a?.length ? a.q8a.join(", ") : "N/A"}</Typography>
                </AccordionDetails>
            </Accordion>

            <Accordion className="ipt-mt-2">
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="ipt-followup-summary"
                >
                    Follow-up Information
                </AccordionSummary>

                <AccordionDetails>
                    <Typography className="ipt-mb-1">
                        <b>Referral made?</b>{" "}
                        {referralMadeRaw === "1" ? "Yes" : referralMadeRaw === "0" ? "No" : "N/A"}
                    </Typography>

                    {referralMadeRaw === "0" && (
                        <Typography className="ipt-mt-1">
                            No referral was made — no follow-up questions apply.
                        </Typography>
                    )}

                    {referralMadeRaw === "1" && (
                        <>
                            <Typography className="ipt-mt-1">
                                Referral(s) Received:
                            </Typography>
                            <List className="ipt-disc-list">
                                {referralObjects.map((referral, index) => (
                                    <ListItem key={index} className="ipt-disc-item">
                                        <ListItemText
                                            primary={<Typography className="ipt-result-primary">{referral[0]}</Typography>}
                                        />
                                        <List className="nextsteps__list--disc">
                                            {referral[1].map((reason, index) => (
                                                <ListItem key={`reason-${index}`} className="ipt-disc-item">
                                                    <ListItemText
                                                        primary={<Typography className="ipt-reason-primary">{reason}</Typography>}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </ListItem>
                                ))}
                            </List>

                            <TextField
                                select
                                fullWidth
                                label="Did the patient seek follow up?"
                                value={fuForm.followup_sought}
                                onChange={(e) =>
                                    setFuForm((p) => ({
                                        ...p,
                                        followup_sought: e.target.value,
                                    }))
                                }
                                className="ipt-field-top-bottom"
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="1">Yes</MenuItem>
                                <MenuItem value="0">No</MenuItem>
                            </TextField>

                            {fuForm.followup_sought === "0" && (
                                <TextField
                                    fullWidth
                                    label="If no, why not?"
                                    value={fuForm.followup_why_not}
                                    onChange={(e) =>
                                        setFuForm((p) => ({
                                            ...p,
                                            followup_why_not: e.target.value,
                                        }))
                                    }
                                    className="ipt-mb-2"
                                />
                            )}

                            {fuForm.followup_sought === "1" && (
                                <>
                                    <TextField
                                        fullWidth
                                        label="If yes, where?"
                                        value={fuForm.followup_where}
                                        onChange={(e) =>
                                            setFuForm((p) => ({
                                                ...p,
                                                followup_where: e.target.value,
                                            }))
                                        }
                                        className="ipt-mb-2"
                                    />
                                    <TextField
                                        fullWidth
                                        label="When did they receive follow up?"
                                        value={fuForm.followup_when}
                                        onChange={(e) =>
                                            setFuForm((p) => ({
                                                ...p,
                                                followup_when: e.target.value,
                                            }))
                                        }
                                        className="ipt-mb-2"
                                    />

                                    <Typography className="ipt-mb-1">
                                        <b>What was the result? (check all that apply)</b>
                                    </Typography>

                                    <FormGroup className="ipt-mb-2">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={fuForm.result_medical}
                                                    onChange={(e) =>
                                                        setFuForm((p) => ({
                                                            ...p,
                                                            result_medical: e.target.checked,
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Needed medical treatment by a physician"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={fuForm.result_hl_confirmed}
                                                    onChange={(e) =>
                                                        setFuForm((p) => ({
                                                            ...p,
                                                            result_hl_confirmed: e.target.checked,
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Hearing loss confirmed"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={fuForm.result_devices}
                                                    onChange={(e) =>
                                                        setFuForm((p) => ({
                                                            ...p,
                                                            result_devices: e.target.checked,
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Patient received hearing assistive device(s) such as hearing aids"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={fuForm.result_vestibular}
                                                    onChange={(e) =>
                                                        setFuForm((p) => ({
                                                            ...p,
                                                            result_vestibular: e.target.checked,
                                                        }))
                                                    }
                                                />
                                            }
                                            label="Vestibular or balance dysfunction confirmed"
                                        />
                                    </FormGroup>
                                </>
                            )}

                            {fuForm.followup_sought === "" && (
                                <Typography className="ipt-mt-1">
                                    Follow-up details have not been recorded yet.
                                </Typography>
                            )}

                            <Button
                                variant="contained"
                                onClick={saveFollowup}
                                className="ipt-mt-1"
                                disabled={savingFollowup}
                            >
                                {savingFollowup ? "Saving..." : "Save Follow-up"}
                            </Button>
                            {saveError && (
                                <Typography className="ipt-mt-1" color="error">
                                    {saveError}
                                </Typography>
                            )}
                        </>
                    )}

                    {referralMadeRaw === "" && (
                        <Typography className="ipt-mt-1">
                            Follow-up has not been recorded yet.
                        </Typography>
                    )}
                </AccordionDetails>
            </Accordion>
        </Container>
    );
}
export default IndividualPatientTest;